import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  IconButton,
  Icons,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import InfiniteScrollTrigger from '../../../common/InfiniteScrollTrigger';
import { OutlookMessage } from './OutlookInbox';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
// import { useMediaQuery } from '@mui/material';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PAGE_SIZE = 4;

type OutlookAllEmailsDialogProps = {
  open: boolean;
  messages: OutlookMessage[];
  onClose: () => void;
  onMessageClick: (threadId: string) => void;
};

const OutlookAllEmailsDialog = ({
  open,
  messages,
  onClose,
  onMessageClick,
}: OutlookAllEmailsDialogProps) => {
  const { palette } = useTheme();
  const isDarkMode = palette.mode === 'dark';
  const dialogBackgroundColor = isDarkMode ? '#16131D' : '#FFFFFF';
  const isXsScreen = useMediaQuery('(max-width:599.95px)');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (open) {
      setVisibleCount(PAGE_SIZE);
      setSearchTerm('');
      setSelectedMessageId(null);
    }
  }, [open]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchTerm]);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const filteredMessages = useMemo(() => {
    if (!normalizedSearchTerm) {
      return messages;
    }

    return messages.filter((message) =>
      [
        message.senderName,
        message.subject,
        message.preview,
        message.receivedLabel,
        message.receivedTime,
        message.receivedDateLabel,
      ]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearchTerm)
    );
  }, [messages, normalizedSearchTerm]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || visibleCount >= filteredMessages.length) {
      return;
    }

    setIsLoadingMore(true);

    // UI-only delay. Replace with an API request later.
    window.setTimeout(() => {
      setVisibleCount((count) =>
        Math.min(count + PAGE_SIZE, filteredMessages.length)
      );
      setIsLoadingMore(false);
    }, 400);
  }, [filteredMessages.length, isLoadingMore, visibleCount]);

  const visibleMessages = filteredMessages.slice(0, visibleCount);
  const hasMore = visibleCount < filteredMessages.length;

  const handleMessageSelect = (message: OutlookMessage) => {
    setSelectedMessageId(message.id);
    onMessageClick(message.threadId);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={isXsScreen}
      aria-labelledby="outlook-all-emails-title"
      PaperProps={{
        sx: {
          maxWidth: '648px',
          width: { xs: '100%', sm: 'calc(100% - 64px)' },
          m: { xs: 0, sm: 4 },
          borderRadius: { xs: 0, sm: 1 },
          backgroundColor: dialogBackgroundColor,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        id="outlook-all-emails-title"
        sx={{
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="subtitle1"
          component="h2"
          fontWeight="medium"
          color="primary"
        >
          All emails
        </Typography>

        <IconButton
          aria-label="Close all emails"
          onClick={onClose}
          size="small"
        >
          <Icons.Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: { xs: 1.5, sm: 2 },
          pt: 0,
          backgroundColor: dialogBackgroundColor,
          backgroundImage: 'none',
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search emails"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        <Box
          ref={scrollContainerRef}
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            pr: 0.5,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {visibleMessages.map((message, index) => (
            <Box key={message.id}>
              <Box
                display="flex"
                gap={1.5}
                py={1.75}
                px={1.5}
                sx={{
                  cursor: 'pointer',
                  bgcolor:
                    selectedMessageId === message.id
                      ? isDarkMode
                        ? '#000000'
                        : 'rgba(69, 33, 136, 0.08)'
                      : 'transparent',
                  borderRadius: '8px',
                  '&:hover': {
                    bgcolor:
                      selectedMessageId === message.id
                        ? isDarkMode
                          ? '#000000'
                          : 'rgba(69, 33, 136, 0.08)'
                        : isDarkMode
                        ? '#000000'
                        : 'rgba(69, 33, 136, 0.04)',
                  },
                }}
                role="button"
                tabIndex={0}
                onClick={() => handleMessageSelect(message)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleMessageSelect(message);
                  }
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  flexShrink={0}
                >
                  <Box
                    width={8}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {message.isUnread ? (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#0277BD',
                        }}
                      />
                    ) : null}
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: message.senderColor,
                      width: 40,
                      height: 40,
                      fontSize: 16,
                      flexShrink: 0,
                    }}
                  >
                    {message.senderInitials}
                  </Avatar>
                </Box>

                <Box minWidth={0} flex={1}>
                  <Box display="flex" justifyContent="space-between" gap={1}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={message.isUnread ? 'bold' : 'medium'}
                      noWrap
                      color="textPrimary"
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      {message.senderName}
                    </Typography>

                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.75}
                      flexShrink={0}
                    >
                      {message.isImportant ? (
                        <PriorityHighIcon
                          sx={{ color: '#D32F2F', fontSize: 24 }}
                        />
                      ) : null}
                      {message.hasAttachment ? (
                        <AttachFileIcon
                          sx={{
                            color: isDarkMode
                              ? 'rgb(208, 188, 250)'
                              : '#6B6B6B',
                            fontSize: 22,
                          }}
                        />
                      ) : null}
                    </Box>
                  </Box>

                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    mt={0.25}
                  >
                    <Typography
                      variant="body2"
                      color="primary"
                      fontWeight={message.isUnread ? 'bold' : 'regular'}
                      noWrap
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      {message.subject}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ flexShrink: 0 }}
                    >
                      {message.receivedLabel === 'Today'
                        ? message.receivedTime
                        : message.receivedLabel === 'Yesterday'
                        ? `Tue ${message.receivedTime ?? ''}`
                        : message.receivedDateLabel || message.receivedLabel}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    mt={0.25}
                    sx={{ fontSize: '14px', lineHeight: '20px' }}
                  >
                    {message.preview}
                  </Typography>
                </Box>
              </Box>
              {index < visibleMessages.length - 1 && (
                <Box
                  sx={{
                    height: '1px',
                    bgcolor: palette.divider,
                    mt: 1,
                  }}
                />
              )}
            </Box>
          ))}

          {isLoadingMore ? (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              py={2}
            >
              Loading more emails…
            </Typography>
          ) : null}

          <InfiniteScrollTrigger
            rootRef={scrollContainerRef}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMore}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OutlookAllEmailsDialog;
